/**
 * Methodology Transparency — L2 calculation audit (spine view).
 *
 * Phase 3 rebuild (memory/REBUILD_PLAN_SPINE_VIEWS.md): replaces the
 * "Calculation Transparency" / "Methodologies" tabs of the retired
 * AuditPipeline monolith. Every number's derivation is a recorded, versioned
 * MethodologyRun: methodology + version, factor set, input datapoints, gaps.
 * James's reverse-trace: pick a run → see exactly what went in and why.
 *
 * Data: GET /v1/spine/runs · /v1/spine/runs/{run_id}
 */
import React, { useEffect, useState } from 'react';
import {
  C, api, Badge, Card, DetailPanel, DrillRow, EmptyState, Err, KV, KPICard,
  Label, Loader, Breadcrumb,
} from '../journeys/shared';
import TracedValue from '../../components/TracedValue';

const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const STATUS_COLOR = { completed: C.emerald, cannot_derive: C.red };
const GRADE_COLOR = {
  measured: C.emerald, calculated: C.blue, estimated: C.amber, declared: C.purple,
};

export default function MethodologyTransparencyPage() {
  const [runs, setRuns] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await api('/v1/spine/runs?limit=200');
      if (r?._error) setError(r.message || 'Failed to load methodology runs');
      else setRuns(r.runs || []);
    })();
  }, []);

  const openRun = async (runId) => {
    if (detail?.run?.run_id === runId) { setDetail(null); return; }
    setDetailLoading(true);
    const d = await api(`/v1/spine/runs/${runId}`);
    setDetail(d?._error ? null : d);
    if (d?._error) setError(d.message || 'Failed to load run detail');
    setDetailLoading(false);
  };

  if (runs === null && !error) {
    return (
      <div style={{ padding: 24, background: C.bg, minHeight: '100vh' }}>
        <Loader text="Loading methodology runs..." />
      </div>
    );
  }

  const completed = (runs || []).filter((r) => r.status === 'completed');
  const blocked = (runs || []).filter((r) => r.status === 'cannot_derive');
  const methodologies = [...new Set((runs || []).map((r) => r.methodology_code))];

  return (
    <div style={{ padding: 24, background: C.bg, minHeight: '100vh', color: C.text }}>
      <Breadcrumb path={[{ label: 'Audit & Defensibility' }, { label: 'Methodology Transparency' }]} />
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 18, fontWeight: 700 }}>Methodology Transparency</div>
        <div style={{ fontSize: 10, color: C.muted }}>
          Every disclosed number derives from a recorded, versioned methodology run — inputs, factor set, and gaps included
        </div>
      </div>

      <Err error={error} />

      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <KPICard label="Recorded runs" value={(runs || []).length} color={C.blue} />
        <KPICard label="Completed" value={completed.length} color={C.emerald} />
        <KPICard label="Cannot derive" value={blocked.length}
          color={blocked.length ? C.red : C.emerald}
          sub={blocked.length ? 'inputs missing — see gaps' : 'no blocked derivations'} />
        <KPICard label="Methodologies" value={methodologies.length}
          sub={methodologies.slice(0, 3).join(' · ')} />
      </div>

      <Card>
        <Label>Methodology runs</Label>
        <div style={{ display: 'flex', padding: '4px 10px', fontSize: 9, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          <span style={{ flex: 2 }}>Methodology</span>
          <span style={{ flex: 1 }}>Framework</span>
          <span style={{ flex: 1.4 }}>Output</span>
          <span style={{ flex: 0.8 }}>Inputs</span>
          <span style={{ flex: 1 }}>Status</span>
          <span style={{ flex: 1.2 }}>Recorded</span>
        </div>
        {(runs || []).map((r) => (
          <DrillRow key={r.run_id} active={detail?.run?.run_id === r.run_id}
            onClick={() => openRun(r.run_id)}>
            <span style={{ flex: 2, fontSize: 11, fontWeight: 600, ...MONO }}>
              {r.methodology_code}
              <span style={{ color: C.muted, fontWeight: 400 }}> v{r.methodology_version}</span>
            </span>
            <span style={{ flex: 1, fontSize: 10, color: C.muted }}>{r.framework}</span>
            <span style={{ flex: 1.4, fontSize: 11, ...MONO }}>
              {r.output_value != null ? `${r.output_value} ${r.output_unit}` : '—'}
            </span>
            <span style={{ flex: 0.8, fontSize: 10, ...MONO }}>
              {(r.input_datapoint_ids || []).length}
            </span>
            <span style={{ flex: 1 }}>
              <Badge text={r.status.toUpperCase()} color={STATUS_COLOR[r.status] || C.dim} />
            </span>
            <span style={{ flex: 1.2, fontSize: 9, color: C.dim, ...MONO }}>
              {r.created_at ? r.created_at.slice(0, 19).replace('T', ' ') : '—'}
            </span>
          </DrillRow>
        ))}
        {runs && !runs.length && (
          <EmptyState
            title="No methodology runs recorded yet"
            message="Runs are recorded automatically when calculations execute over spine datapoints. Once a number is derived, its full derivation appears here." />
        )}
      </Card>

      {detailLoading && <Loader text="Loading derivation..." />}

      {detail && (
        <DetailPanel
          title={`${detail.run.methodology_code} v${detail.run.methodology_version}`}
          subtitle={`run ${detail.run.run_id} — step-by-step derivation`}
          color={STATUS_COLOR[detail.run.status] || C.blue}
          onClose={() => setDetail(null)}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <Label>Run</Label>
              <KV label="Run ID" value={<span style={MONO}>{detail.run.run_id}</span>} />
              <KV label="Framework" value={detail.run.framework} />
              <KV label="Cycle" value={<span style={MONO}>{detail.run.cycle_id}</span>} />
              <KV label="Factor set" value={<span style={MONO}>{detail.run.factor_set_ref || '—'}</span>} />
              <KV label="Output"
                value={<span style={MONO}>{detail.run.output_value != null ? `${detail.run.output_value} ${detail.run.output_unit}` : '—'}</span>}
                color={STATUS_COLOR[detail.run.status]} />
              <KV label="Status" value={detail.run.status}
                color={STATUS_COLOR[detail.run.status]} />
              {(detail.run.gaps || []).length > 0 && (
                <>
                  <div style={{ marginTop: 8 }}><Label>Gaps (why derivation blocked)</Label></div>
                  {detail.run.gaps.map((g, i) => (
                    <KV key={i} label={g.input || `gap ${i + 1}`} value={g.reason || '—'} color={C.red} />
                  ))}
                </>
              )}
            </div>
            <div>
              <Label>Input datapoints ({detail.inputs.length})</Label>
              {detail.inputs.map((d) => (
                <div key={d.datapoint_id} style={{ padding: '5px 0', borderBottom: `1px solid ${C.border}20`, fontSize: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 600 }}>{d.metric_code}</span>
                    {/* Phase 4: every input value traces to its evidence chain */}
                    <TracedValue value={d.value} unit={d.unit} type="datapoint"
                      id={d.datapoint_id} style={{ fontSize: 10 }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: C.muted, fontSize: 9 }}>
                    <span style={MONO}>{d.datapoint_id}</span>
                    <Badge text={d.confidence_grade.toUpperCase()}
                      color={GRADE_COLOR[d.confidence_grade] || C.dim} />
                  </div>
                </div>
              ))}
              {!detail.inputs.length && (
                <div style={{ fontSize: 10, color: C.dim }}>No inputs resolved</div>
              )}
              {(detail.missing_inputs || []).length > 0 && (
                <div style={{ marginTop: 8, padding: 8, borderRadius: 5, background: C.red + '15', border: `1px solid ${C.red}33` }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.red, marginBottom: 4 }}>
                    MISSING INPUTS — AUDIT FINDING
                  </div>
                  {detail.missing_inputs.map((m) => (
                    <div key={m} style={{ fontSize: 9, color: C.red, ...MONO }}>{m}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DetailPanel>
      )}
    </div>
  );
}
